
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');

  const plans = [
    {
      name: 'Solo Tradie',
      description: 'Perfect for sole traders getting started',
      monthlyPrice: 0,
      annualPrice: 0,
      features: [
        '5 quotes per month',
        'Basic material calculator',
        'PDF export',
        'Email support',
        'Mobile app access',
      ],
      cta: 'Start Free Trial',
      popular: false,
    },
    {
      name: 'Growing Team',
      description: 'Everything you need to scale your trade business',
      monthlyPrice: 49,
      annualPrice: 290,
      features: [
        'Unlimited quotes',
        'AI material take-off',
        'Branded PDF exports',
        'Profit calculator',
        'Team collaboration',
        'Priority support',
        'Supplier integrations',
        'GPS tracking',
      ],
      cta: 'Start Free Trial',
      popular: true,
      roi: 'Pays for itself after 1 job',
    },
    {
      name: 'Enterprise Builder',
      description: 'For larger teams and construction businesses',
      monthlyPrice: 149,
      annualPrice: 890,
      features: [
        'Everything in Growing Team',
        'Unlimited team members',
        'Custom branding',
        'API access',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced analytics',
        'White-label options',
      ],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const calculateSavings = (monthly: number, annual: number) => {
    return monthly * 12 - annual;
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-white" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-2">
            Simple, Transparent <span className="text-amber-500">Pricing</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
            Choose the plan that fits your business. All plans include a 14-day free trial.
          </p>

          {/* Billing Toggle - Mobile Optimized */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-2">
            <span className={`text-sm sm:text-base font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className="relative w-14 h-7 sm:w-16 sm:h-8 bg-gray-200 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 touch-manipulation"
              style={{ backgroundColor: billingCycle === 'annual' ? '#F59E0B' : '#E5E7EB' }}
            >
              <span
                className="absolute top-1 left-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full shadow-md transition-transform duration-300"
                style={{ transform: billingCycle === 'annual' ? 'translateX(28px)' : 'translateX(0)' }}
              ></span>
            </button>
            <span className={`text-sm sm:text-base font-medium ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <span className="bg-green-100 text-green-700 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">
                Save 20%
              </span>
            )}
          </div>
        </div>

        {/* Pricing Cards - Mobile Optimized */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                plan.popular ? 'ring-2 ring-amber-500 lg:scale-105' : 'border border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="bg-amber-500 text-black text-xs sm:text-sm font-bold text-center py-2 px-4">
                  MOST POPULAR
                </div>
              )}

              <div className="p-6 sm:p-8">
                {/* Plan Header */}
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6">{plan.description}</p>

                {/* Pricing */}
                <div className="mb-6">
                  {billingCycle === 'monthly' ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl sm:text-5xl font-bold text-gray-900">
                          ${plan.monthlyPrice}
                        </span>
                        <span className="text-base sm:text-lg text-gray-600">/month</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-4xl sm:text-5xl font-bold text-gray-900">
                          ${Math.round(plan.annualPrice / 12)}
                        </span>
                        <span className="text-base sm:text-lg text-gray-600">/month</span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500">
                        ${plan.annualPrice}/year
                      </p>
                      {plan.monthlyPrice > 0 && (
                        <p className="text-xs sm:text-sm text-green-600 font-semibold mt-1">
                          Save ${calculateSavings(plan.monthlyPrice, plan.annualPrice)}/year
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* ROI Badge */}
                {plan.roi && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 sm:px-4 py-2 sm:py-3 mb-6">
                    <p className="text-xs sm:text-sm font-semibold text-amber-800 text-center">
                      💰 {plan.roi}
                    </p>
                  </div>
                )}

                {/* CTA Button - Mobile Optimized */}
                <Link
                  to={plan.cta === 'Contact Sales' ? '/contact' : '/signup'}
                  className={`block w-full text-center font-bold text-base sm:text-lg px-6 py-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap touch-manipulation min-h-[56px] flex items-center justify-center ${
                    plan.popular
                      ? 'bg-amber-500 hover:bg-amber-600 text-black'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  {plan.cta}
                </Link>

                {/* Features List */}
                <ul className="mt-6 sm:mt-8 space-y-3 sm:space-y-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-lg sm:text-xl text-green-500 flex-shrink-0 mt-0.5"></i>
                      <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges - Mobile Optimized */}
        <div className="bg-gray-50 rounded-2xl p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="flex flex-col items-center text-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-xl sm:text-2xl text-green-600"></i>
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base text-gray-900">Secure Payment</p>
                <p className="text-xs sm:text-sm text-gray-600">256-bit SSL encryption</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="ri-bank-card-line text-xl sm:text-2xl text-blue-600"></i>
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base text-gray-900">All Cards Accepted</p>
                <p className="text-xs sm:text-sm text-gray-600">Visa, Mastercard, Amex</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <i className="ri-map-pin-line text-xl sm:text-2xl text-amber-600"></i>
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base text-gray-900">Australian Business</p>
                <p className="text-xs sm:text-sm text-gray-600">ABN: 12 345 678 901</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="ri-refund-line text-xl sm:text-2xl text-purple-600"></i>
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base text-gray-900">30-Day Guarantee</p>
                <p className="text-xs sm:text-sm text-gray-600">Pro-rated refunds available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
